import React, { useEffect, useState } from 'react';
import GenderInput from './GenderInput';
import Chatbot from './Chatbot';
import { calculateNaming } from '../services/api';
import { localizeChineseText } from '../services/chineseText';

const emptyForm = {
  child_birth_date: '',
  child_birth_time: '',
  child_is_male: true,
  father_birth_date: '',
  father_birth_time: '',
  surname: '',
  preferred_text: '',
};

const parseBirth = (dateValue, timeValue, isMale = true) => {
  const rawDate = String(dateValue || '').trim();
  const isoDateMatch = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const legacyDateMatch = rawDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const timeMatch = String(timeValue || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if ((!isoDateMatch && !legacyDateMatch) || !timeMatch) return null;
  const year = isoDateMatch ? Number(isoDateMatch[1]) : Number(legacyDateMatch[3]);
  const month = isoDateMatch ? Number(isoDateMatch[2]) : Number(legacyDateMatch[2]);
  const day = isoDateMatch ? Number(isoDateMatch[3]) : Number(legacyDateMatch[1]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);
  const date = new Date(year, month - 1, day);
  const validDate = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  const validTime = hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;
  if (!validDate || !validTime) return null;
  return { year, month, day, hour, minute, is_male: isMale };
};

const splitChars = (value) => Array.from(String(value || '').replace(/\s+/g, ''));

const WUXING_KEYS = [
  ['木', 'wood'],
  ['火', 'fire'],
  ['土', 'earth'],
  ['金', 'metal'],
  ['水', 'water'],
];

const primaryGridKeys = ['renge', 'dige', 'zongge'];

const scoreToneClass = (score = 0) => {
  if (score >= 85) return 'is-great';
  if (score >= 72) return 'is-good';
  if (score >= 58) return 'is-steady';
  return 'is-caution';
};

const gridSummary = (candidate, txt) => {
  const grids = candidate.five_grids || [];
  return grids
    .filter((grid) => primaryGridKeys.includes(grid.key))
    .map((grid) => `${txt(grid.title)} ${grid.number}${txt(grid.fortune)}`)
    .join(' · ');
};

const NamingPage = ({ t, lang = 'zh-TW' }) => {
  const [form, setForm] = useState({ ...emptyForm });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [namingProgress, setNamingProgress] = useState(0);
  const [error, setError] = useState('');
  const txt = (value) => localizeChineseText(value || '', lang);

  useEffect(() => {
    if (!loading) {
      setNamingProgress(0);
      return undefined;
    }

    setNamingProgress(8);
    const timer = window.setInterval(() => {
      setNamingProgress((current) => {
        if (current < 28) return current + 6;
        if (current < 56) return current + 4;
        if (current < 82) return current + 3;
        return Math.min(current + 1, 94);
      });
    }, 1100);

    return () => window.clearInterval(timer);
  }, [loading]);

  const setSurname = (value) => {
    setForm({ ...form, surname: value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const birth = parseBirth(form.child_birth_date, form.child_birth_time, form.child_is_male);
    const fatherBirth = parseBirth(form.father_birth_date, form.father_birth_time, true);
    const surname = splitChars(form.surname).join('');
    if (!birth || !fatherBirth || !surname) {
      setError(t('namingValidationError'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const computed = await calculateNaming({
        birth: { ...birth, lang },
        father_birth: { ...fatherBirth, lang },
        surname,
        preferred_text: form.preferred_text,
        candidate_count: 10,
        lang,
      });
      setResult(computed);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      setError(t('namingCalcError'));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm({ ...emptyForm });
    setResult(null);
    setError('');
  };

  const renderProfile = (title, profile) => {
    if (!profile) return null;
    const pillars = profile.pillars || {};
    const counts = profile.counts || {};
    return (
      <article className="fc-card naming-profile-card">
        <span className="fc-eyebrow">{title}</span>
        <h3>{txt(profile.day_master)} · {txt(profile.zodiac)}</h3>
        <p className="bz-note">{t('namingPillars')}: {txt(pillars.year)}、{txt(pillars.month)}、{txt(pillars.day)}、{txt(pillars.hour)}</p>
        <div className="naming-wuxing-bars">
          {WUXING_KEYS.map(([label]) => (
            <div className="naming-wuxing-row" key={label}>
              <span>{txt(label)}</span>
              <meter min="0" max="8" value={counts[label] || 0} />
              <strong>{counts[label] || 0}</strong>
            </div>
          ))}
        </div>
        <div className="fc-defs">
          <div className="fc-def"><span className="fc-def__key">{t('namingDominantElement')}</span><span className="fc-def__val">{txt(profile.dominant) || t('none')}</span></div>
          <div className="fc-def"><span className="fc-def__key">{t('namingMissingElements')}</span><span className="fc-def__val">{txt((profile.missing || []).join('、')) || t('none')}</span></div>
          <div className="fc-def"><span className="fc-def__key">{t('usefulElements')}</span><span className="fc-def__val">{txt((profile.useful_elements || []).join('、')) || t('none')}</span></div>
        </div>
      </article>
    );
  };

  const profile = result?.naming_profile;
  const candidates = result?.name_candidates || [];
  const renderCharacterNotes = (candidate) => {
    const meanings = Array.isArray(candidate.character_meanings) ? candidate.character_meanings : [];
    const fortunes = Array.isArray(candidate.character_fortunes) ? candidate.character_fortunes : [];
    const chars = splitChars(candidate.given_name);
    if (!chars.length) return null;

    return (
      <div className="naming-character-grid">
        {chars.map((char) => {
          const meaning = meanings.find((item) => item.char === char) || {};
          const fortune = fortunes.find((item) => item.char === char) || {};
          return (
            <div className="naming-character-card" key={`${candidate.full_name}-${char}`}>
              <strong>{txt(char)}</strong>
              <p><span>{t('namingCharacterMeaning')}</span>{txt(meaning.meaning || meaning.analysis || '')}</p>
              <p><span>{t('namingCharacterFortune')}</span>{txt([fortune.fortune, fortune.analysis].filter(Boolean).join('：'))}</p>
            </div>
          );
        })}
      </div>
    );
  };
  const progressStage = namingProgress < 30
    ? t('namingProgressChart')
    : namingProgress < 58
      ? t('namingProgressGrid')
      : namingProgress < 84
        ? t('namingProgressNames')
        : t('namingProgressFinal');

  return (
    <div className="bz-naming-page">
      <section className="bz-intro bz-intro--compact fc-rise">
        <a className="fc-btn fc-btn--ghost fc-btn--sm" href="/bazi/">{t('backToIntegrated')}</a>
        <h1 className="bz-intro__title">{t('namingPageTitle')}</h1>
        <p className="bz-intro__desc">{t('namingPageDesc')}</p>
      </section>

      {!result && (
        <div className="bz-form-card naming-form-card">
          <form className="bz-form naming-form" onSubmit={handleSubmit}>
            <section className="fc-card naming-form-section">
              <h2>{t('namingBirthTitle')}</h2>
              <div className="compat-date-grid">
                <div className="fc-field">
                  <label className="fc-label" htmlFor="naming-child-birth-date">{t('compatBirthDateLabel')}</label>
                  <input id="naming-child-birth-date" className="fc-input" type="date" min="1900-01-01" max="2100-12-31" value={form.child_birth_date} onChange={(event) => setForm({ ...form, child_birth_date: event.target.value })} />
                </div>
                <div className="fc-field">
                  <label className="fc-label" htmlFor="naming-child-birth-time">{t('compatBirthTimeLabel')}</label>
                  <input id="naming-child-birth-time" className="fc-input" type="time" step="60" value={form.child_birth_time} onChange={(event) => setForm({ ...form, child_birth_time: event.target.value })} placeholder={t('compatBirthTimePlaceholder')} />
                </div>
              </div>
              <GenderInput value={form.child_is_male} onChange={(value) => setForm({ ...form, child_is_male: value })} t={t} />
            </section>

            <section className="fc-card naming-form-section">
              <h2>{t('namingFatherBirthTitle')}</h2>
              <div className="compat-date-grid">
                <div className="fc-field">
                  <label className="fc-label" htmlFor="naming-father-birth-date">{t('compatBirthDateLabel')}</label>
                  <input id="naming-father-birth-date" className="fc-input" type="date" min="1900-01-01" max="2100-12-31" value={form.father_birth_date} onChange={(event) => setForm({ ...form, father_birth_date: event.target.value })} />
                </div>
                <div className="fc-field">
                  <label className="fc-label" htmlFor="naming-father-birth-time">{t('compatBirthTimeLabel')}</label>
                  <input id="naming-father-birth-time" className="fc-input" type="time" step="60" value={form.father_birth_time} onChange={(event) => setForm({ ...form, father_birth_time: event.target.value })} placeholder={t('compatBirthTimePlaceholder')} />
                </div>
              </div>
            </section>

            <section className="fc-card naming-form-section">
              <h2>{t('namingNameTitle')}</h2>
              <div className="naming-name-grid">
                <div className="fc-field">
                  <label className="fc-label" htmlFor="naming-surname">{t('namingSurname')}</label>
                  <input id="naming-surname" className="fc-input" value={form.surname} onChange={(event) => setSurname(event.target.value)} placeholder={t('namingSurnamePlaceholder')} />
                </div>
                <div className="fc-field">
                  <label className="fc-label" htmlFor="naming-preferred">{t('namingPreferredText')}</label>
                  <textarea id="naming-preferred" className="fc-input naming-textarea" value={form.preferred_text} onChange={(event) => setForm({ ...form, preferred_text: event.target.value })} placeholder={t('namingPreferredPlaceholder')} rows="4" />
                </div>
              </div>
            </section>

            {error && <div className="fc-error" role="alert">{error}</div>}
            <div className="bz-form__actions">
              <button type="submit" className="fc-btn fc-btn--primary fc-btn--lg" disabled={loading}>
                {loading ? t('namingCalculating') : t('namingCalculate')}
              </button>
              <button type="button" className="fc-btn fc-btn--ghost" onClick={reset}>{t('reset')}</button>
            </div>
            {loading && (
              <div className="naming-progress" role="status" aria-live="polite">
                <div className="naming-progress__head">
                  <span>{t('namingProgressTitle')}</span>
                  <strong>{namingProgress}%</strong>
                </div>
                <progress className="progress naming-progress__bar" value={namingProgress} max="100" aria-label={t('namingProgressTitle')}></progress>
                <p>{progressStage}</p>
              </div>
            )}
          </form>
        </div>
      )}

      {result && (
        <section className="bz-result naming-result">
          <div className="bz-recap">
            <div className="bz-recap__info">
              <span className="fc-eyebrow">{t('namingPageTitle')}</span>
              <span className="bz-recap__value fc-num">
                {form.surname}
                <span className="bz-recap__sep">·</span>
                {form.child_birth_date} {form.child_birth_time}
              </span>
            </div>
            <button type="button" className="fc-btn fc-btn--secondary fc-btn--sm" onClick={() => setResult(null)}>
              {t('namingEdit')}
            </button>
          </div>

          <section className="bz-block">
            <div className="fc-card naming-hero">
              <div>
                <span className="fc-eyebrow">{t('namingOverall')}</span>
                <h2>{t('namingCandidatesTitle')}</h2>
                <p>{txt(profile?.generation_note)}</p>
                <div className="naming-tag-row">
                  {(profile?.supplement_elements || []).map((element) => <span className="fc-tag fc-tag--accent" key={element}>{txt(element)}</span>)}
                </div>
              </div>
              <div className="compat-score" aria-label={`${t('namingGeneratedCount')} ${candidates.length}`}>
                <strong>{candidates.length}</strong>
                <span>{t('namingGeneratedCount')}</span>
              </div>
            </div>
          </section>

          <section className="bz-block">
            <h2 className="bz-section-title">{t('namingWuxingProfileTitle')}</h2>
            <div className="bz-grid-2">
              {renderProfile(t('namingChildProfile'), profile?.child_profile)}
              {renderProfile(t('namingFatherProfile'), profile?.father_profile)}
            </div>
          </section>

          <section className="bz-block">
            <h2 className="bz-section-title">{t('namingCandidatesTitle')}</h2>
            <div className="naming-candidate-list">
              {candidates.map((candidate) => (
                <article className="fc-card naming-candidate-card" key={`${candidate.rank}-${candidate.full_name}`}>
                  <div className="naming-candidate-card__head">
                    <div>
                      <span className="fc-eyebrow">No. {candidate.rank}</span>
                      <h3>{txt(candidate.full_name)}</h3>
                      <p>{txt(candidate.pronunciation)}</p>
                    </div>
                    <div className={`compat-score compat-score--sm naming-score ${scoreToneClass(candidate.score)}`} aria-label={`${t('namingAuspiciousScore')} ${candidate.score}`}>
                      <strong>{candidate.score}</strong>
                      <span>{t('namingScoreUnit')}</span>
                    </div>
                  </div>

                  <div className="naming-candidate-meaning">
                    <strong>{t('namingNameMeaning')}</strong>
                    <p>{txt(candidate.name_meaning || candidate.meaning_analysis || candidate.reason)}</p>
                  </div>

                  {(candidate.name_story || candidate.preference_fit) && (
                    <div className="naming-analysis-grid naming-analysis-grid--story">
                      {candidate.preference_fit && <div><strong>{t('namingPreferenceFit')}</strong><p>{txt(candidate.preference_fit)}</p></div>}
                      {candidate.name_story && <div><strong>{t('namingNameStory')}</strong><p>{txt(candidate.name_story)}</p></div>}
                    </div>
                  )}

                  {renderCharacterNotes(candidate)}

                  <div className="naming-judgement-strip">
                    <div>
                      <span>{t('namingAuspiciousScore')}</span>
                      <strong>{candidate.score}/100 · {txt(candidate.grade)}</strong>
                    </div>
                    <div>
                      <span>{t('namingThreeTalentTitle')}</span>
                      <strong>{txt(candidate.three_talents?.sequence)} · {txt(candidate.three_talents?.tone)}</strong>
                    </div>
                    <div>
                      <span>{t('namingCoreGrids')}</span>
                      <strong>{gridSummary(candidate, txt)}</strong>
                    </div>
                  </div>

                  <p className="naming-candidate-reason"><strong>{t('namingReason')}</strong>{txt(candidate.reason)}</p>
                  <div className="naming-analysis-grid">
                    <div><strong>{t('namingMeaningAnalysis')}</strong><p>{txt(candidate.meaning_analysis)}</p></div>
                    <div><strong>{t('namingHomophoneAnalysis')}</strong><p>{txt(candidate.homophone_analysis)}</p></div>
                    <div><strong>{t('namingZodiacAnalysis')}</strong><p>{txt(candidate.zodiac_analysis)}</p></div>
                    <div><strong>{t('namingShapeAnalysis')}</strong><p>{txt(candidate.shape_analysis)}</p></div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <Chatbot baziContext={result} t={t} lang={lang} />
        </section>
      )}
    </div>
  );
};

export default NamingPage;
